import { z } from 'zod';

import { getEmailListSchema } from '@/modules/common/validations/email-list-schema';

import { InvoiceEntityType } from '../types';
import {
  InvoiceLineValidationInput,
  InvoiceLineValidationInputType,
  InvoiceLineValidationIssue,
  InvoiceLineValidationIssueCode,
  InvoiceValidationIssueCode,
  validateInvoiceItems,
  validateInvoiceLine,
} from './invoice-item-validation';

const REQUIRED_MESSAGE = 'authenticated.invoices:details.edit.validation.required';

export const UPDATE_INVOICE_LINE_MESSAGE_KEY_BY_ISSUE_CODE: Record<
  InvoiceLineValidationIssueCode,
  string
> = {
  [InvoiceLineValidationIssueCode.MissingMember]:
    'authenticated.invoices:details.edit.validation.memberRequired',
  [InvoiceLineValidationIssueCode.InvalidQuantity]:
    'authenticated.invoices:details.edit.validation.invalidQuantity',
  [InvoiceLineValidationIssueCode.InvalidUnitPrice]:
    'authenticated.invoices:details.edit.validation.invalidUnitPrice',
  [InvoiceLineValidationIssueCode.ZeroMemberLine]:
    'authenticated.invoices:details.edit.validation.zeroUserLine',
  [InvoiceLineValidationIssueCode.EmptyDescription]:
    'authenticated.invoices:details.edit.validation.descriptionRequired',
  [InvoiceLineValidationIssueCode.DescriptionTooLong]:
    'authenticated.invoices:details.edit.validation.descriptionTooLong',
};

export const UPDATE_INVOICE_MESSAGE_BY_ISSUE: Record<InvoiceValidationIssueCode, string> = {
  [InvoiceValidationIssueCode.EmptyItems]: 'authenticated.invoices:details.edit.validation.items',
  [InvoiceValidationIssueCode.NegativeTotal]:
    'authenticated.invoices:details.edit.validation.total',
};

const invoicePeriodSchema = z
  .object({
    from: z.date().optional(),
    to: z.date().nullable().optional(),
  })
  .refine(period => period.from && period.to, { message: REQUIRED_MESSAGE })
  .refine(period => !period.from || !period.to || period.from <= period.to, {
    message: 'authenticated.invoices:details.edit.validation.period',
  });

/** QuickBooks limits for the receiver emails of an invoice */
const INVOICE_TO_EMAILS_MAX_LENGTH = 100;
const INVOICE_COPY_EMAILS_MAX_LENGTH = 200;
const INVALID_EMAILS_MESSAGE = 'common:error.emailListInvalid';

const copyEmailsSchema = getEmailListSchema({
  maxLength: INVOICE_COPY_EMAILS_MAX_LENGTH,
  maxLengthMessage: 'authenticated.invoices:details.edit.validation.copyEmailsTooLong',
  invalidMessage: INVALID_EMAILS_MESSAGE,
});

const receiverEmailsSchema = z.object({
  to: getEmailListSchema({
    maxLength: INVOICE_TO_EMAILS_MAX_LENGTH,
    maxLengthMessage: 'authenticated.invoices:details.edit.validation.toEmailsTooLong',
    invalidMessage: INVALID_EMAILS_MESSAGE,
  }),
  cc: copyEmailsSchema,
  bcc: copyEmailsSchema,
});

const invoiceItemShape = z.object({
  entityType: z.nativeEnum(InvoiceEntityType),
  entityId: z.string(),
  description: z.string(),
  quantity: z.string(),
  unitPrice: z.string(),
  amount: z.string(),
});

type InvoiceItemFormValues = z.infer<typeof invoiceItemShape>;

export const mapUpdateInvoiceItemToValidationInput = (
  item: InvoiceItemFormValues
): InvoiceLineValidationInput | null => {
  if (item.entityType === InvoiceEntityType.User) {
    return {
      type: InvoiceLineValidationInputType.Member,
      memberId: item.entityId,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
    };
  }

  if (item.entityType === InvoiceEntityType.Overhead) {
    return {
      type: InvoiceLineValidationInputType.Overhead,
      description: item.description,
      unitPrice: item.unitPrice,
    };
  }

  return null;
};

export const getUpdateInvoiceItemValidationIssues = (
  item: InvoiceItemFormValues
): InvoiceLineValidationIssue[] => {
  const input = mapUpdateInvoiceItemToValidationInput(item);
  return input ? validateInvoiceLine(input) : [];
};

export const getUpdateInvoiceValidationIssues = (
  items: InvoiceItemFormValues[]
): InvoiceValidationIssueCode[] =>
  validateInvoiceItems(
    items.flatMap(item => {
      const input = mapUpdateInvoiceItemToValidationInput(item);
      return input ? [input] : [];
    })
  );

const invoiceItemSchema = invoiceItemShape.superRefine((item, context) => {
  const issues = getUpdateInvoiceItemValidationIssues(item);
  issues.forEach(issue => {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: [issue.field === 'memberId' ? 'entityId' : issue.field],
      message: UPDATE_INVOICE_LINE_MESSAGE_KEY_BY_ISSUE_CODE[issue.code],
    });
  });

  if (issues.length > 0 || mapUpdateInvoiceItemToValidationInput(item)) return;

  context.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['entityType'],
    message: 'authenticated.invoices:details.edit.validation.unsupportedItem',
  });
});

export type UpdateInvoiceItemFormValues = z.infer<typeof invoiceItemSchema>;

export const updateInvoiceValidationSchema = z
  .object({
    period: invoicePeriodSchema,
    issuedDate: z.date({ required_error: REQUIRED_MESSAGE }).optional(),
    paymentTerm: z.string().min(1, REQUIRED_MESSAGE),
    currencyCode: z.string().regex(/^[A-Z]{3}$/, REQUIRED_MESSAGE),
    paymentDetailsId: z.number().nullable(),
    receiverEmails: receiverEmailsSchema,
    items: z.array(invoiceItemSchema),
  })
  .superRefine((values, context) => {
    if (!values.issuedDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['issuedDate'],
        message: REQUIRED_MESSAGE,
      });
    }

    const invoiceIssues = getUpdateInvoiceValidationIssues(values.items);

    invoiceIssues.forEach(issue => {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items'],
        message: UPDATE_INVOICE_MESSAGE_BY_ISSUE[issue],
      });
    });
  });

export type UpdateInvoiceFormValues = z.infer<typeof updateInvoiceValidationSchema>;
